import React, { createContext, useEffect, useState } from 'react';
import * as PropTypes from 'prop-types';
import { collection, doc, onSnapshot, addDoc, deleteDoc, getFirestore } from 'firebase/firestore';
import { useFirebase } from '../firebase/firebase-provider';
import { useDataStore } from './datastore-provider';

interface InvitationsContextType {
    homeInvitations: any[] | null;
    removeHomeInvitation: (id: any) => void;
    sendInvite: (userId: any, invite: any) => Promise<unknown>;
}

const InvitationsContext = createContext<InvitationsContextType | undefined>(undefined);

export const useInvitations = () => {
    const context = React.useContext(InvitationsContext);
    if (context === undefined) {
        throw new Error('useInvitations must be used within an InvitationsProvider');
    }
    return context;
};

const COLLECTION_NAME = 'invitations';

export const INVITATION_TYPE = {
    HOME: 'HOME',
};

InvitationsProvider.propTypes = {
    children: PropTypes.element.isRequired,
};
export function InvitationsProvider(props: any) {
    const [homeInvitations, setHomeInvitations] = useState(null);
    const app = useFirebase();
    const { userDb, getUserStore } = useDataStore();

    useEffect(() => {
        if (userDb) {
            const invitationsCollection = collection(userDb, COLLECTION_NAME);

            const unsubscribe = onSnapshot(
                invitationsCollection,
                (querySnapshot: any) => {
                    loadInvitations(querySnapshot.docs || []);
                },
                (error: any) => {
                    console.debug(error);
                    setHomeInvitations([]);
                    // TODO: Let the user know something went wrong
                }
            );
            return unsubscribe;
        } else if (userDb === null) {
            setHomeInvitations([]);
        } else {
            setHomeInvitations(null);
        }
    }, [userDb]);

    function loadInvitations(docs: any) {
        const homeInvites: any = [];
        docs.forEach((doc: any) => {
            const invite = { ...doc.data(), id: doc.id };
            switch (invite.type) {
            case INVITATION_TYPE.HOME:
                homeInvites.push(invite);
                break;
            default:
            }
        });
        setHomeInvitations(homeInvites);
        return docs.map((doc: any) => ({
            ...doc.data(),
            id: doc.id
        }));
    }

    function removeHomeInvitation(id: any) {
        if (userDb) {
            const invitationDoc = doc(userDb, COLLECTION_NAME, id);
            deleteDoc(invitationDoc)
                .catch(console.debug); // TODO: show error message
        }
    }

    function sendInvite(userId: any, invite: any) {
        const store = getUserStore(userId);
        if (store) {
            return new Promise(((resolve) => {
                const invitationsCollection = collection(store, COLLECTION_NAME);
                addDoc(invitationsCollection, invite)
                    .then(resolve);
            }));
        }
        return Promise.reject(new Error('Failed to send invite'));
    }

    return (
        <InvitationsContext.Provider value={{ homeInvitations, removeHomeInvitation, sendInvite }}>
            { props.children }
        </InvitationsContext.Provider>
    );
}
